import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import { User, Mail, Phone, Lock, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore';
import PageHeading from '../../components/PageHeading';

function AdminForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const isEdit = id != null && id !== 'new' && String(id) !== 'undefined';

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    name: '',
    email: '',
    phone: '',
    role: 'ADMIN',
    is_active: true,
  });

  const { data: admin, isLoading } = useQuery({
    queryKey: ['admin', id],
    queryFn: async () => {
      const response = await api.get(`/admins/${id}`);
      return response.data;
    },
    enabled: isEdit,
  });

  useEffect(() => {
    if (admin) {
      setFormData({
        username: admin.username || '',
        password: '',
        confirmPassword: '',
        name: admin.name || '',
        email: admin.email || '',
        phone: admin.phone || '',
        role: admin.role || 'ADMIN',
        is_active: admin.is_active !== false,
      });
    }
  }, [admin]);

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const { confirmPassword, ...payload } = data;
      const response = await api.post('/admins', payload);
      return response.data;
    },
    onSuccess: (created) => {
      toast.success('Admin created');
      queryClient.invalidateQueries(['admins']);
      navigate(`/admins/${created.id}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to create admin');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      const { confirmPassword, ...payload } = data;
      if (!payload.password) delete payload.password;
      const response = await api.put(`/admins/${id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Admin updated');
      queryClient.invalidateQueries(['admins']);
      queryClient.invalidateQueries(['admin', id]);
      navigate(`/admins/${id}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update admin');
    },
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.username?.trim()) {
      toast.error('Username is required');
      return;
    }
    if (!formData.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!isEdit && !formData.password) {
      toast.error('Password is required');
      return;
    }
    if (formData.password && formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (isEdit) updateMutation.mutate(formData);
    else createMutation.mutate(formData);
  };

  if (isEdit && isLoading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  const canAssignSuper = user?.role === 'SUPER_ADMIN';

  return (
    <div>
      <PageHeading
        title={isEdit ? 'Edit Admin' : 'Add Admin'}
        backTo="/admins"
        backLabel="Back to Admins"
        infoTitle={isEdit ? 'Edit Admin' : 'Add Admin'}
        infoChildren={<p>Each person should use their own login for audit trails.</p>}
      />

      <form onSubmit={handleSubmit} noValidate className="bg-card rounded-lg shadow p-6 space-y-6">
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline mr-2" />
            Username *
          </label>
          <input
            type="text"
            id="username"
            name="username"
            required
            value={formData.username}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="e.g. carl"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            <Lock className="w-4 h-4 inline mr-2" />
            Password {!isEdit && '*'}
          </label>
          <input
            type="password"
            id="password"
            name="password"
            required={!isEdit}
            value={formData.password}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={isEdit ? 'Leave blank to keep current password' : 'Enter password'}
          />
          <p className="text-xs text-gray-500 mt-1">
            Same password as Staff ??Password. Web admin login uses this username.
          </p>
        </div>

        {formData.password && (
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              <Lock className="w-4 h-4 inline mr-2" />
              Confirm Password *
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              required={!!formData.password}
              value={formData.confirmPassword}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Confirm password"
            />
          </div>
        )}

        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline mr-2" />
            Full Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Enter full name"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="w-4 h-4 inline mr-2" />
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="name@company.co.nz"
          />
          <p className="text-xs text-gray-500 mt-1">You can also log in with this email.</p>
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="w-4 h-4 inline mr-2" />
            Phone
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Enter phone number"
          />
        </div>

        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
            <Shield className="w-4 h-4 inline mr-2" />
            Role *
          </label>
          <select
            id="role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="ADMIN">ADMIN</option>
            <option value="MANAGER">MANAGER</option>
            {canAssignSuper && <option value="SUPER_ADMIN">SUPER_ADMIN</option>}
          </select>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_active"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            className="h-4 w-4 text-primary-600 border-gray-300 rounded"
          />
          <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
            Active
          </label>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
            className="px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {isEdit ? 'Save changes' : 'Create admin'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admins')}
            className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminForm;
